import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { hydrateFavourites } from "./favourites";
import { createSearchMap, registerSearchMarker, sizeSearchMap } from "./searchMap";

/**
 * Promise that resolves when the Google Maps JS API is ready.
 * Handles both timing scenarios:
 * - API loaded before app bundle: checks flag set by inline callback
 * - API loaded after app bundle: listens for the custom DOM event
 */
export const googleMapsReady = new Promise((resolve) => {
    if (window.google?.maps || window._googleMapsReady) {
        resolve();
    } else {
        window.addEventListener('google-maps-ready', resolve, { once: true });
    }
});

/**
 * Inject the Maps JS API. src comes from the landed-houses-google-maps handle
 * (src/Actions/Assets.php), which the templates needing maps still enqueue - so
 * a page that never asks for a map has no URL here and can never load it.
 */
export function loadGoogleMapsApi() {
    if (window._lhGoogleMapsRequested || !window.LHGoogleMapsSrc) {
        return;
    }

    window._lhGoogleMapsRequested = true;

    const script = document.createElement('script');

    script.src = window.LHGoogleMapsSrc;
    script.async = true;

    document.head.append(script);
}

/**
 * The API costs 1.13MB on a phone – a 600KB WASM labeler, webgl.js, style tables
 * and Google's own fonts – and on a property page the map sits far below the
 * fold, so most visitors were paying for something they never saw. Load it only
 * once something that needs it is close to the viewport: the 400px margin keeps
 * it effectively eager where the map is already on screen (search results, the
 * dashboard) and lazy where it isn't.
 */
export function loadGoogleMapsWhenNeeded() {
    const elements = document.querySelectorAll('[data-map], [data-address-search]');

    if (!elements.length || window._lhGoogleMapsRequested) {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        loadGoogleMapsApi();

        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            loadGoogleMapsApi();
            observer.disconnect();
        }
    }, {
        rootMargin: '400px',
    });

    elements.forEach((element) => observer.observe(element));

    // START THE DOWNLOAD ON THE CLICK, NOT ON ARRIVAL.
    //
    // The observer fires when the map is within 400px of the viewport, which
    // on a property page is most of the way through the smooth scroll from the
    // address link - so the 1.13MB only begins downloading as the map appears,
    // and the visitor watches an empty box while it arrives. Anything marked
    // data-preload-map starts it at the moment of the click instead, buying
    // the whole scroll animation as a head start.
    //
    // Free at page load: this is bound, not fetched. A visitor who never taps
    // the link downloads exactly what they do today, which is the whole reason
    // the map is lazy in the first place.
    //
    // `click` rather than `pointerdown`, deliberately. Pointerdown would fire
    // ~100ms earlier, but on a phone it ALSO fires when a finger lands on the
    // link to scroll the page - and that would spend 1.13MB of somebody's data
    // on a gesture that was never a tap.
    document.querySelectorAll('[data-preload-map]').forEach((trigger) => {
        trigger.addEventListener('click', () => loadGoogleMapsApi(), { once: true });
    });
}

function initMap(element) {
    const data = JSON.parse(element.getAttribute('data-map') || '{}');
    let markers = element.querySelectorAll('[data-marker]');

    // The search map's height depends on where it starts on the page, so set
    // it BEFORE the map exists - the fit below works out its zoom from the
    // box it is given, and a box that changes afterwards gets the wrong one.
    if (data.search === true) {
        sizeSearchMap(element.closest('[data-ajax-results-map]'));
    }

    const options = {
        zoom: data.zoom !== undefined ? parseInt(data.zoom) : 9,
        zoomControl: data.zoomControl !== undefined ? data.zoomControl : true,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: data.disableUi !== undefined ? data.disableUi : false,
        streetViewControl: false,
        fullscreenControlOptions: false,
        fullscreenControl: false,
        center: new google.maps.LatLng(0, 0),
        scrollwheel: data.scrollwheel !== undefined ? data.scrollwheel : false,
        mapId: "23639de14b2df22a",
        markers: [],
        cluster: true,
    };

    // Opt-in only, through the data-map settings. Left unset, Google's
    // default applies - two-finger panning on a phone, which is what the
    // property page and the owner's form have always had. The search map asks
    // for 'greedy' so one finger pans and the wheel zooms. scrollwheel above
    // is opt-in for the same reason: false stays the default, and passing
    // false alongside 'greedy' would leave the wheel dead.
    if (data.gestureHandling) {
        options.gestureHandling = data.gestureHandling;
    }

    // The search map fits itself to a fractional zoom (fitToMarkers() in
    // searchMap.js), and Google only honours one when this is on - it
    // defaults to off unless the map renders as vector. Left alone on every
    // other map.
    if (data.search === true) {
        options.isFractionalZoomEnabled = true;
    }

    const map = new google.maps.Map(element, options);

    const search = data.search === true ? createSearchMap(element, map) : null;

    markers.forEach(function (marker) {
        initMarker(JSON.parse(marker.getAttribute('data-marker')), marker.innerHTML, map, search);
    });

    if (data.cluster !== undefined && data.cluster === true) {
        const clustererRenderer = {
            render: ({ count, position }) => new google.maps.marker.AdvancedMarkerElement({
                position,
                zIndex: map.markers.length + count,
                content: markerContent('cluster', count),
            }),
        };

        new MarkerClusterer({ map: map, markers: map.markers, renderer: clustererRenderer });
    }

    // The search map fits itself (fractional zoom, see fitToMarkers() in
    // searchMap.js); every other map keeps fitBounds, exactly as before.
    if (search) {
        search.refit();
    } else {
        centerMap(map);
    }

    window.LandedHouses.maps = window.LandedHouses.maps || {};

    window.LandedHouses.maps[data.name] = map;

    return map;
}

function markerContent(type = 'std', txt = '') {
    const element = document.createElement('span');

    element.classList.add('google-map__marker', 'google-map__marker--' + type);

    if (txt) {
        element.textContent = txt;
    }

    return element;
}

var LHPrevGoogleMapMarker = null;

/**
 * @param {object|null} search The search-map state from createSearchMap(), or
 *                             null for every other map. Optional, so existing
 *                             callers are unaffected.
 */
export function initMarker(data, html, map, search = null) {
    const marker = new google.maps.marker.AdvancedMarkerElement({
        position: {
            lat: parseFloat(data.lat),
            lng: parseFloat(data.lng),
        },
        map: map,
        content: markerContent(data.type, data.txt),
    });

    map.markers.push(marker);

    // The search map handles its own cards (a bottom sheet on phones, one
    // shared InfoWindow on desktop) - see searchMap.js.
    if (search) {
        registerSearchMarker(search, parseInt(data.id, 10), marker, html);

        return;
    }

    if (html) {

        const infowindow = new google.maps.InfoWindow({
            content: html,
        });

        google.maps.event.addListener(marker, 'click', function () {
            if (LHPrevGoogleMapMarker) {
                LHPrevGoogleMapMarker.close();
            }

            LHPrevGoogleMapMarker = infowindow;

            infowindow.open(map, marker);

            setTimeout(() => {
                hydrateFavourites();
            }, 10);
        });
    }
}

function centerMap(map) {
    const bounds = new google.maps.LatLngBounds();

    map.markers.forEach(function (marker) {
        bounds.extend({
            lat: marker.position.lat,
            lng: marker.position.lng,
        });
    });

    //---- Single marker.
    if (map.markers.length == 1) {
        map.setCenter(bounds.getCenter());
    } else {
        map.fitBounds(bounds);
    }
}

export default async function googleMaps() {
    const elements = document.querySelectorAll('[data-map]');

    if (!elements.length) {
        return;
    }

    // Size the search map NOW, before the 1.13MB API has arrived, so the box
    // is already the right height when the map draws into it rather than
    // jumping from the CSS fallback. initMap() measures again just before
    // creating the map, in case anything above it moved meanwhile.
    elements.forEach((element) => {
        if (JSON.parse(element.getAttribute('data-map') || '{}').search === true) {
            sizeSearchMap(element.closest('[data-ajax-results-map]'));
        }
    });

    loadGoogleMapsWhenNeeded();

    try {
        await googleMapsReady;

        elements.forEach(function (element) {
            initMap(element);

            element.classList.add('loaded');
        });
    } catch (error) {
        console.error('Google Maps failed to initialise:', error);
    }
}
